import { TreeType } from '@/components/Layers/modal/types';
import {
    CanvaFont,
    ImportLayersElements,
    ImportLayersElementsPage,
    LayerElementsMetadata
} from '@/typings/layers/layers';
import { IFileParserStrategy } from '@/typings/parsers/externalFileParser';
import { FileParserType } from '@/typings/parsers/fileParserTypes';
import { mmToPixels } from '@/utils/unitsUtils';
import { useColor } from '@/composables/color';
import { ColorObject } from '@/typings/libraries';

export interface AmazonJsonFontOption {
    identifier: string;
    family: string;
    fontType?: string;
    style?: string;
}

export interface AmazonJsonColorOption {
    identifier: string;
    name?: string;
    value: string;
    colorModel?: string;
}

export interface AmazonJsonImage {
    imageUrl: string;
    dimension?: { width: number; height: number };
}

export interface AmazonJsonChooserOption {
    identifier: string;
    name?: string;
    label?: string;
    overlayImage?: AmazonJsonImage;
    thumbnailImage?: AmazonJsonImage;
}

export interface AmazonJsonNode {
    identifier?: string;
    type?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    maxLines?: number;
    dimension?: { width: number; height: number };
    position?: { x: number; y: number };
    rotation?: { angleOfRotation: number };
    baseImage?: { imageUrl?: string; dimension?: { width: number; height: number } };
    defaultFontIdentifier?: string;
    fontOptions?: AmazonJsonFontOption[];
    defaultColorIdentifier?: string;
    colorOptions?: AmazonJsonColorOption[];
    options?: AmazonJsonChooserOption[];
    children?: AmazonJsonNode[];
    [key: string]: unknown;
}

export interface AmazonJsonParsedResult {
    artworkName: string;
    artWorkThumbnail: string;
    fontsCssUrl?: string;
    surfaces: AmazonJsonNode[];
}

interface PlacementStyleContext {
    font?: CanvaFont;
    fontOptions?: CanvaFont[];
    colorFill?: ColorObject;
    colorOptions?: ColorObject[];
}

interface CollectedPlacement {
    node: AmazonJsonNode;
    context: PlacementStyleContext;
}

export class AmazonJsonFileParser implements IFileParserStrategy<AmazonJsonParsedResult> {
    type: FileParserType = 'AMAZON_FILE_JSON';
    page = 1;
    // sellerTemplate positions/dimensions are in millimeters; kept consistent with the DPI
    // used by AmazonExcelFileParser so both importers scale the same way.
    private readonly DPI = 300;
    private readonly DEFAULT_PAGE_SIZE = 400;
    private readonly FONT_SIZE_RATIO = 0.8;
    private readonly color = useColor();

    async parse(buffer: ArrayBuffer): Promise<AmazonJsonParsedResult> {
        const text = new TextDecoder('utf-8').decode(buffer);
        const raw = JSON.parse(text) as Record<string, unknown>;

        // Different Amazon Custom exports carry the template tree under different keys
        // (sellerTemplate on product-detail exports, sellerConfig/transformedSellerConfig on
        // seller-config exports), either as a JSON string or already parsed.
        const rootRaw = raw.sellerTemplate ?? raw.sellerConfig ?? raw.transformedSellerConfig;
        const root = (typeof rootRaw === 'string' ? JSON.parse(rootRaw) : rootRaw) as AmazonJsonNode | undefined;

        const productSummary = raw.productSummary as { sku?: string; productImageUrl?: string } | undefined;

        const surfaces = (root?.children || []).filter((child) => child.type === 'PreviewContainerComponent');

        return {
            artworkName:
                (raw.sku as string) || productSummary?.sku || root?.label || root?.name || 'Amazon Custom Template',
            artWorkThumbnail: (raw.productImageUrl as string) || productSummary?.productImageUrl || '',
            fontsCssUrl: raw.fontsCssUrl as string | undefined,
            surfaces: surfaces.length ? surfaces : root?.children || []
        };
    }

    async extractLayers(
        result: AmazonJsonParsedResult,
        importLayers: ImportLayersElements
    ): Promise<ImportLayersElements> {
        const selectedPage = importLayers?.selectedPage || this.page;
        const pages = result.surfaces.map((surface, index) => this.buildPage(surface, index));

        return {
            ...importLayers,
            type: this.type,
            mode: 'LAYERS',
            artWorkThumbnail: importLayers?.artWorkThumbnail || result.artWorkThumbnail,
            artworkName: result.artworkName,
            includeDimensions: importLayers?.includeDimensions ?? true,
            fontsCssUrl: result.fontsCssUrl,
            selectedPage,
            pages: pages.length ? pages : [this.emptyPage()]
        };
    }

    private buildPage(surface: AmazonJsonNode, index: number): ImportLayersElementsPage {
        const placements = this.collectPlacements(surface);
        const placementElements = placements
            .map(({ node, context }, elementIndex) => this.buildElement(node, context, elementIndex))
            .filter((element): element is LayerElementsMetadata => element !== null);
        const { width, height } = this.pageSizeFromPlacements(placements.map((p) => p.node));

        const imageChoosers = this.collectImageChoosers(surface);
        const imageChooserElements = imageChoosers
            .map((chooser, chooserIndex) =>
                this.buildImageChooserElement(chooser, width, height, placementElements.length + chooserIndex)
            )
            .filter((element): element is LayerElementsMetadata => element !== null);

        return {
            pageNumber: index + 1,
            name: surface.label || surface.name || `Surface ${index + 1}`,
            fileUrl: surface.baseImage?.imageUrl,
            artWorkWidth: width,
            artWorkHeight: height,
            elements: [...placementElements, ...imageChooserElements]
        };
    }

    /**
     * Some OptionChoosers (e.g. avatar layers like body/clothes/hair) offer a real, pre-supplied
     * image per option instead of collecting buyer input — these have no dimension/position of
     * their own (unlike PlacementContainerComponent), so they're detected separately by the
     * presence of an overlayImage on their options.
     */
    private collectImageChoosers(node: AmazonJsonNode | undefined, acc: AmazonJsonNode[] = []): AmazonJsonNode[] {
        if (!node) return acc;

        if (node.type === 'OptionChooserComponent' && node.options?.some((option) => option.overlayImage?.imageUrl)) {
            acc.push(node);
            return acc;
        }

        if (Array.isArray(node.children)) {
            node.children.forEach((child) => this.collectImageChoosers(child, acc));
        }

        return acc;
    }

    private buildImageChooserElement(
        chooser: AmazonJsonNode,
        pageWidth: number,
        pageHeight: number,
        zIndex: number
    ): LayerElementsMetadata | null {
        const options = (chooser.options || []).filter((option) => option.overlayImage?.imageUrl);
        if (!options.length) return null;

        const defaultImage = options[0].overlayImage as AmazonJsonImage;
        const width = defaultImage.dimension?.width || pageWidth;
        const height = defaultImage.dimension?.height || pageHeight;
        const name = chooser.name || chooser.label || 'Image';

        return {
            width,
            height,
            // Centered: these layers carry no position of their own, unlike mm-based placements.
            left: (pageWidth - width) / 2,
            top: (pageHeight - height) / 2,
            rotation: 0,
            visible: true,
            name,
            imgUrl: defaultImage.imageUrl,
            imageOptions: options.map((option) => option.overlayImage!.imageUrl),
            type: TreeType.FROM_FILE_IMAGE_ELEMENT,
            zIndex
        } as LayerElementsMetadata;
    }

    /**
     * sellerTemplate has no explicit physical surface size in mm, and baseImage.dimension
     * is just a low-res preview thumbnail unrelated to the mm coordinate space used by
     * placements. Deriving the page size from the placements' bounding box keeps element
     * positions internally consistent with the page, mirroring how AmazonExcelFileParser
     * converts everything through the same mm -> px scale.
     */
    private pageSizeFromPlacements(placements: AmazonJsonNode[]): { width: number; height: number } {
        if (!placements.length) {
            return { width: this.DEFAULT_PAGE_SIZE, height: this.DEFAULT_PAGE_SIZE };
        }

        const maxXMm = Math.max(...placements.map((p) => (p.position?.x || 0) + (p.dimension?.width || 0)));
        const maxYMm = Math.max(...placements.map((p) => (p.position?.y || 0) + (p.dimension?.height || 0)));

        return {
            width: mmToPixels(maxXMm, this.DPI),
            height: mmToPixels(maxYMm, this.DPI)
        };
    }

    private emptyPage(): ImportLayersElementsPage {
        return {
            pageNumber: 1,
            name: 'Surface 1',
            artWorkWidth: this.DEFAULT_PAGE_SIZE,
            artWorkHeight: this.DEFAULT_PAGE_SIZE,
            elements: []
        };
    }

    /**
     * PlacementContainerComponent nodes are the only ones carrying dimension/position,
     * i.e. the only ones that render on the canvas. Selectors (Option/Color/FontChooser)
     * configure the buyer wizard but have no placement of their own, so they're skipped —
     * except that a Font/ColorChooser found among a node's children applies to its sibling
     * placements (e.g. "Texts" groups a FontChooser, a ColorChooser and the actual text
     * blocks together), so that default is carried down as context while recursing.
     */
    private collectPlacements(
        node: AmazonJsonNode | undefined,
        context: PlacementStyleContext = {},
        acc: CollectedPlacement[] = []
    ): CollectedPlacement[] {
        if (!node) return acc;

        if (node.type === 'PlacementContainerComponent' && node.dimension && node.position) {
            acc.push({ node, context });
            return acc;
        }

        if (Array.isArray(node.children)) {
            const scopedContext = this.mergeStyleContext(context, node.children);
            node.children.forEach((child) => this.collectPlacements(child, scopedContext, acc));
        }

        return acc;
    }

    private mergeStyleContext(context: PlacementStyleContext, siblings: AmazonJsonNode[]): PlacementStyleContext {
        const fontChooser = siblings.find((sibling) => sibling.type === 'FontChooserComponent');
        const colorChooser = siblings.find((sibling) => sibling.type === 'ColorChooserComponent');
        const fontOptions = fontChooser && this.extractFontOptions(fontChooser);
        const colorOptions = colorChooser && this.extractColorOptions(colorChooser);

        return {
            font: (fontChooser && this.extractDefaultFont(fontChooser)) || context.font,
            fontOptions: fontOptions?.length ? fontOptions : context.fontOptions,
            colorFill: (colorChooser && this.extractDefaultColor(colorChooser)) || context.colorFill,
            colorOptions: colorOptions?.length ? colorOptions : context.colorOptions
        };
    }

    private extractDefaultFont(node: AmazonJsonNode): CanvaFont | undefined {
        const options = node.fontOptions || [];
        const option = options.find((f) => f.identifier === node.defaultFontIdentifier) || options[0];
        return option ? this.toCanvaFont(option) : undefined;
    }

    private extractFontOptions(node: AmazonJsonNode): CanvaFont[] {
        const options = node.fontOptions || [];
        return options.map((option) => this.toCanvaFont(option));
    }

    private toCanvaFont(option: AmazonJsonFontOption): CanvaFont {
        return {
            family: option.family,
            style: option.style || 'Regular',
            rawTypeface: option.family
        };
    }

    private extractDefaultColor(node: AmazonJsonNode): ColorObject | undefined {
        const options = node.colorOptions || [];
        const option = options.find((c) => c.identifier === node.defaultColorIdentifier) || options[0];
        return option?.value ? this.toColorObject(option.value) : undefined;
    }

    private extractColorOptions(node: AmazonJsonNode): ColorObject[] {
        const options = node.colorOptions || [];
        return options
            .map((option) => (option.value ? this.toColorObject(option.value) : undefined))
            .filter((color): color is ColorObject => color !== undefined);
    }

    private toColorObject(value: string): ColorObject {
        const hexValue = value.startsWith('#') ? value : `#${value}`;
        const { cmyk, hex, a } = this.color.getColorFromString(hexValue);
        return { hex, alpha: a, cmyk: this.color.cmykToDecimalString(cmyk) };
    }

    private findLeafInput(node: AmazonJsonNode): AmazonJsonNode | null {
        if (node.type === 'TextInputComponent' || node.type === 'ImageInputComponent') return node;

        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                const found = this.findLeafInput(child);
                if (found) return found;
            }
        }

        return null;
    }

    private buildElement(
        placement: AmazonJsonNode,
        context: PlacementStyleContext,
        zIndex: number
    ): LayerElementsMetadata | null {
        const leaf = this.findLeafInput(placement);
        if (!leaf || !placement.dimension || !placement.position) return null;

        const isImage = leaf.type === 'ImageInputComponent';
        const width = mmToPixels(placement.dimension.width, this.DPI);
        const height = mmToPixels(placement.dimension.height, this.DPI);
        // Kept as top/left: the import consumer (createStepOneCtrl.js) derives
        // centerX/centerY itself as left + width / 2, top + height / 2.
        const left = mmToPixels(placement.position.x, this.DPI);
        const top = mmToPixels(placement.position.y, this.DPI);
        const name = placement.name || placement.label || leaf.label || leaf.type || 'Element';
        const maxLines = leaf.maxLines && leaf.maxLines > 1 ? leaf.maxLines : 1;
        // The import consumer (createStepOneCtrl.js, isAmazonDesign branch) treats this as a
        // pixel size and converts it to points via fabric.util.pxToPt, so it must scale with
        // the placement's own height rather than being a flat constant.
        const fontSize = Math.round((height / maxLines) * this.FONT_SIZE_RATIO);

        return {
            width,
            height,
            left,
            top,
            rotation: placement.rotation?.angleOfRotation || 0,
            visible: true,
            name,
            textValue: isImage ? name : leaf.placeholder || leaf.label || name,
            type: isImage ? TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT : TreeType.FROM_FILE_TEXT_ELEMENT,
            fontSize,
            multiline: maxLines > 1,
            font: !isImage ? context.font : undefined,
            fontOptions: !isImage ? context.fontOptions : undefined,
            colorFill: !isImage ? context.colorFill : undefined,
            colorOptions: !isImage ? context.colorOptions : undefined,
            zIndex
        } as LayerElementsMetadata;
    }
}
